/**
 * Custom hook for API calls with retry logic, cancellation, and error handling
 */

import { useRef, useCallback } from 'react';
import { logger } from '@/utils/logger';

interface RetryOptions {
  maxRetries?: number;
  retryDelay?: number;
  retryCondition?: (error: unknown) => boolean;
}

interface ApiCallOptions extends RetryOptions {
  signal?: AbortSignal;
}

/**
 * Retry a function with exponential backoff
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = 3,
    retryDelay = 1000,
    retryCondition = () => true,
  } = options;

  let lastError: unknown;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry if aborted
      if (error instanceof Error && error.name === 'AbortError') {
        throw error;
      }
      
      // Check if we should retry
      if (attempt < maxRetries && retryCondition(error)) {
        const delay = retryDelay * Math.pow(2, attempt); // Exponential backoff
        logger.debug(`Retrying API call`, { attempt: attempt + 1, maxRetries, delay });
        await new Promise(resolve => setTimeout(resolve, delay));
      } else {
        break;
      }
    }
  }
  
  throw lastError;
}

/**
 * Hook for making API calls with retry and cancellation
 */
export function useApiCall() {
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());

  const callApi = useCallback(
    async <T>(
      key: string,
      apiFunction: (signal?: AbortSignal) => Promise<T>,
      options: ApiCallOptions = {}
    ): Promise<T> => {
      // Cancel previous request with same key
      const existingController = abortControllersRef.current.get(key);
      if (existingController) {
        existingController.abort();
      }

      // Create new AbortController
      const abortController = new AbortController();
      abortControllersRef.current.set(key, abortController);

      // Use provided signal or create new one
      const signal = options.signal || abortController.signal;

      try {
        const result = await withRetry(
          () => apiFunction(signal),
          {
            maxRetries: options.maxRetries,
            retryDelay: options.retryDelay,
            retryCondition: options.retryCondition,
          }
        );
        
        return result;
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          logger.debug('API call aborted', { key });
          throw error;
        }
        throw error;
      } finally {
        abortControllersRef.current.delete(key);
      }
    },
    []
  );

  const cancelApiCall = useCallback((key: string) => {
    const controller = abortControllersRef.current.get(key);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(key);
    }
  }, []);

  const cancelAllApiCalls = useCallback(() => {
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
  }, []);

  return {
    callApi,
    cancelApiCall,
    cancelAllApiCalls,
  };
}

/**
 * Default retry condition - retry on network errors or 5xx errors
 */
export function defaultRetryCondition(error: unknown): boolean {
  if (error instanceof Error) {
    // Network errors
    if (error.message.includes('fetch') || error.message.includes('network')) {
      return true;
    }
  }
  
  // Check if it's an API error with status code
  if (typeof error === 'object' && error !== null) {
    const apiError = error as { statusCode?: number; status?: number };
    const status = apiError.statusCode || apiError.status;
    if (status && status >= 500 && status < 600) {
      return true;
    }
  }
  
  return false;
}









